import { CardShell } from '@atlas/ui-components'
import { Button, cn } from '@atlas/ui-primitives'
import { useRef, useState } from 'react'
import type { BuilderEdge, BuilderNode, DirectionLine } from '@/domain/workflow-builder-layout'
import { directionLinesFor, GRID_SIZE, NODE_HEIGHT, NODE_WIDTH, snapToGrid } from '@/domain/workflow-builder-layout'
import { PHASE_LABEL, PHASE_TAG_CLASS } from '@/domain/workflow-labels'

export type BuilderSelection =
	| { type: 'node'; id: string }
	| { type: 'edge'; edgeKey: string; dir: 'forward' | 'backward' }
	| null

interface WorkflowBuilderCanvasProps {
	nodes: readonly BuilderNode[]
	edges: readonly BuilderEdge[]
	selection: BuilderSelection
	connecting: boolean
	connectFromId: string | null
	onNodeClick: (id: string) => void
	/** `onError` (optional), called if the persisted move is rejected, so the drag session can roll its local "settled" override back rather than diverge from the server forever. */
	onNodeMove: (id: string, x: number, y: number, onError?: () => void) => void
	onEdgeLabelClick: (edgeKey: string, dir: 'forward' | 'backward') => void
	onToggleConnect: () => void
	onNewStatus: () => void
}

interface DragSession {
	id: string
	startX: number
	startY: number
	dx: number
	dy: number
}

/** How far the pointer must travel before a press counts as a drag rather than a click. */
const DRAG_THRESHOLD_PX = 4

/**
 * Free-form node dragging via pointer capture (no window-level listeners
 * needed — capture redirects move/up to the same element regardless of where
 * the cursor ends up). `disabled` (connect mode) forces every release to
 * resolve as a click, since connect mode never repositions a node.
 */
function useNodeDrag(
	nodesById: Map<string, BuilderNode>,
	onNodeMove: (id: string, x: number, y: number, onError?: () => void) => void,
	onNodeClick: (id: string) => void,
	disabled: boolean,
) {
	const [drag, setDrag] = useState<DragSession | null>(null)
	const dragRef = useRef<DragSession | null>(null)
	// The dropped position, held locally until the server's own `x`/`y` catches
	// up. Without this, clearing `drag` on drop falls straight back to the
	// still-stale server position (a visible snap to the OLD spot), and only
	// jumps to where it was actually dropped once the PATCH resolves and the
	// query refetches — the exact "snaps back, then jumps" the drag feels like.
	const [settled, setSettled] = useState<Record<string, { x: number; y: number }>>({})
	const settledRef = useRef(settled)
	settledRef.current = settled
	// Bumped on every drop, per node — lets a drag's own rollback recognize a
	// NEWER drag on the same node has already happened by the time it fires,
	// so an out-of-order failure never clobbers a later, already-settled
	// position with a stale one.
	const generationRef = useRef<Record<string, number>>({})

	const onPointerDown = (id: string, e: React.PointerEvent<HTMLButtonElement>) => {
		// Keeps receiving move/up even if a fast drag carries the pointer off the
		// (smallish) node hitbox. Best-effort: jsdom (tests) has no real implementation.
		try {
			e.currentTarget.setPointerCapture(e.pointerId)
		} catch {
			// no-op
		}
		const session: DragSession = { id, startX: e.clientX, startY: e.clientY, dx: 0, dy: 0 }
		dragRef.current = session
		setDrag(session)
	}
	const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
		if (!dragRef.current) return
		const next = { ...dragRef.current, dx: e.clientX - dragRef.current.startX, dy: e.clientY - dragRef.current.startY }
		dragRef.current = next
		setDrag(next)
	}
	const onPointerUp = () => {
		const session = dragRef.current
		dragRef.current = null
		setDrag(null)
		if (!session) return
		const moved = !disabled && Math.hypot(session.dx, session.dy) >= DRAG_THRESHOLD_PX
		if (moved) {
			// A still-pending settled position (the server hasn't confirmed the
			// PREVIOUS drag yet) is the correct base for this one — falling back to
			// `nodesById` (raw server data) here would compute this drag's delta
			// from a stale pre-first-drag position.
			const previousSettled = settledRef.current[session.id]
			const base = previousSettled ?? nodesById.get(session.id)
			if (base) {
				const x = snapToGrid(base.x + session.dx)
				const y = snapToGrid(base.y + session.dy)
				setSettled((prev) => ({ ...prev, [session.id]: { x, y } }))
				const generation = (generationRef.current[session.id] ?? 0) + 1
				generationRef.current[session.id] = generation
				onNodeMove(session.id, x, y, () => {
					// A newer drag on this same node has already happened since this
					// one dropped — its own settled position (successful or not) is
					// what should stand, not this now-out-of-order rollback.
					if (generationRef.current[session.id] !== generation) return
					// The server never confirmed this position — roll back to whatever
					// was settled before this failed drag (or drop the override
					// entirely, falling back to real server data) rather than leave
					// the node permanently pinned somewhere that was never persisted.
					setSettled((prev) => {
						if (previousSettled) return { ...prev, [session.id]: previousSettled }
						const { [session.id]: _dropped, ...rest } = prev
						return rest
					})
				})
			}
		}
		// A drag also selects the node it moved, same as most diagram tools; in
		// connect mode this is always the click branch (source/target picking).
		onNodeClick(session.id)
	}

	return { drag, settled, onPointerDown, onPointerMove, onPointerUp }
}

function NodeView({
	node,
	selected,
	isConnectSource,
	onPointerDown,
	onPointerMove,
	onPointerUp,
}: {
	node: BuilderNode
	selected: boolean
	isConnectSource: boolean
	onPointerDown: (e: React.PointerEvent<HTMLButtonElement>) => void
	onPointerMove: (e: React.PointerEvent<HTMLButtonElement>) => void
	onPointerUp: (e: React.PointerEvent<HTMLButtonElement>) => void
}) {
	return (
		<button
			type="button"
			draggable={false}
			onPointerDown={onPointerDown}
			onPointerMove={onPointerMove}
			onPointerUp={onPointerUp}
			onPointerCancel={onPointerUp}
			style={{ position: 'absolute', left: node.x, top: node.y, width: NODE_WIDTH, height: NODE_HEIGHT }}
			className={cn(
				'flex flex-col justify-center gap-1 rounded-ds-md border bg-ds-surface-elevated px-3 py-2 text-left shadow-ds-sm transition-colors select-none touch-none cursor-grab active:cursor-grabbing',
				selected ? 'border-ds-primary ring-2 ring-ds-ring' : 'border-ds-border',
				isConnectSource && 'border-ds-accent ring-2 ring-ds-accent/40',
			)}
		>
			<div className="flex items-center justify-between gap-2">
				<span
					className={cn(
						'rounded-ds-full px-2 py-0.5 font-mono text-ds-4xs uppercase tracking-wide',
						PHASE_TAG_CLASS[node.phase],
					)}
				>
					{PHASE_LABEL[node.phase]}
				</span>
				<span className="flex gap-1">
					{node.isInitial ? <span className="font-mono text-ds-4xs text-ds-text-subtle">INITIAL</span> : null}
					{node.isTerminal ? <span className="font-mono text-ds-4xs text-ds-text-subtle">TERMINAL</span> : null}
				</span>
			</div>
			<div className="truncate text-sm font-medium text-ds-text">{node.label}</div>
			<div className="truncate font-mono text-ds-4xs text-ds-text-subtle">{node.code}</div>
		</button>
	)
}

/** Forward (progressing) vs backward (regressing) — color is the only signal; a circle terminator at the target end, no arrowhead, no text on the canvas itself. */
const LINE_STROKE_CLASS: Record<'forward' | 'backward', string> = {
	forward: 'stroke-ds-success',
	backward: 'stroke-ds-warning',
}
const LINE_FILL_CLASS: Record<'forward' | 'backward', string> = {
	forward: 'fill-ds-success',
	backward: 'fill-ds-warning',
}

function DirectionLineView({
	line,
	selected,
	onClick,
}: {
	line: DirectionLine
	selected: boolean
	onClick: () => void
}) {
	return (
		<g
			role="button"
			tabIndex={0}
			aria-label={line.ctaLabel}
			className="cursor-pointer"
			onClick={onClick}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') onClick()
			}}
		>
			<title>{line.ctaLabel}</title>
			{/* Wide transparent line first — a bigger, easier hit target than the thin visible stroke drawn on top of it. */}
			<line x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} stroke="transparent" strokeWidth={14} />
			<line
				x1={line.x1}
				y1={line.y1}
				x2={line.x2}
				y2={line.y2}
				className={LINE_STROKE_CLASS[line.dir]}
				strokeWidth={selected ? 4 : 2.5}
			/>
			<circle cx={line.x2} cy={line.y2} r={selected ? 5 : 4} className={LINE_FILL_CLASS[line.dir]} />
		</g>
	)
}

function EdgeLineLegend() {
	return (
		<div className="mt-2 flex gap-4 font-mono text-ds-4xs uppercase tracking-wide text-ds-text-subtle">
			<span className="flex items-center gap-1.5">
				<span className="h-0.5 w-4 rounded-ds-full bg-ds-success" /> Forward
			</span>
			<span className="flex items-center gap-1.5">
				<span className="h-0.5 w-4 rounded-ds-full bg-ds-warning" /> Backward
			</span>
		</div>
	)
}

/**
 * The workflow-builder canvas — freely-draggable status nodes connected by
 * colored transition lines (green = forward, amber = backward; color is the
 * only signal — no dot, no CTA text on the canvas itself, which still shows
 * on hover and in the inspector). `onNodeMove` reports a node's final dropped
 * position; the caller persists it (`workflow_status.canvasX/Y`).
 */
export function WorkflowBuilderCanvas({
	nodes,
	edges,
	selection,
	connecting,
	connectFromId,
	onNodeClick,
	onNodeMove,
	onEdgeLabelClick,
	onToggleConnect,
	onNewStatus,
}: WorkflowBuilderCanvasProps) {
	const nodesById = new Map(nodes.map((n) => [n.id, n]))
	const { drag, settled, onPointerDown, onPointerMove, onPointerUp } = useNodeDrag(
		nodesById,
		onNodeMove,
		onNodeClick,
		connecting,
	)
	const displayNodes = nodes.map((n) => {
		const settledPosition = settled[n.id]
		const base = settledPosition ? { ...n, ...settledPosition } : n
		return drag && n.id === drag.id
			? { ...base, x: snapToGrid(base.x + drag.dx), y: snapToGrid(base.y + drag.dy) }
			: base
	})
	const displayNodesById = new Map(displayNodes.map((n) => [n.id, n]))
	const directionLines = edges.flatMap((e) => directionLinesFor(e, displayNodesById))
	const maxX = Math.max(NODE_WIDTH + 40, ...displayNodes.map((n) => n.x + NODE_WIDTH + 40))
	const maxY = Math.max(NODE_HEIGHT + 40, ...displayNodes.map((n) => n.y + NODE_HEIGHT + 40))

	return (
		<div className="min-w-0 flex-1">
			<div className="mb-2.5 flex flex-wrap items-center justify-between gap-3">
				<p className="font-mono text-ds-3xs text-ds-text-subtle">
					{connecting
						? connectFromId
							? 'Click the target status to connect.'
							: 'Click a status to start a transition.'
						: 'Drag a status to move it on the canvas (layout only - reorder the steps in the inspector). Click to edit, or click a transition line.'}
				</p>
				<div className="flex gap-2">
					<Button variant="secondary" className="rounded-ds-full" onPress={onNewStatus}>
						＋ New status
					</Button>
					<Button variant={connecting ? 'primary' : 'secondary'} className="rounded-ds-full" onPress={onToggleConnect}>
						⇢ Draw transition
					</Button>
				</div>
			</div>

			<CardShell
				className="relative h-135 overflow-auto p-0"
				style={{
					backgroundImage: 'radial-gradient(var(--color-ds-border) 1px, transparent 1px)',
					backgroundSize: `${GRID_SIZE}px ${GRID_SIZE}px`,
				}}
			>
				<div className="relative" style={{ width: maxX, height: maxY }}>
					<svg width={maxX} height={maxY} className="absolute top-0 left-0">
						{directionLines.map((l) => (
							<DirectionLineView
								key={l.id}
								line={l}
								selected={selection?.type === 'edge' && selection.edgeKey === l.edgeKey && selection.dir === l.dir}
								onClick={() => onEdgeLabelClick(l.edgeKey, l.dir)}
							/>
						))}
					</svg>

					{displayNodes.map((n) => (
						<NodeView
							key={n.id}
							node={n}
							selected={selection?.type === 'node' && selection.id === n.id}
							isConnectSource={connecting && connectFromId === n.id}
							onPointerDown={(e) => onPointerDown(n.id, e)}
							onPointerMove={onPointerMove}
							onPointerUp={onPointerUp}
						/>
					))}
				</div>
			</CardShell>

			<EdgeLineLegend />
		</div>
	)
}
